import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import React, { Fragment, useEffect, useState } from "react";
import { LuImagePlus } from "react-icons/lu";
import Select from "react-select";
import {
  addVehicle,
  updateVehicle,
  getAllVehicleTypes,
  getActiveMobileLocations,
} from "../../../../api/vehicle.api";
import { getCurrentUser } from "../../../../api/user.api";
import { errorMsg, success } from "../../../../utils/notifications";
import { baseURL } from "../../../../api/api";

const AddVehicle = ({
  open,
  setOpen,
  fetchVehicles,
  initialData,
  vehiclesData,
}) => {
  const [addVehicleData, setAddVehicleData] = useState(
    initialData || vehiclesData
  );
  const [ownerId, setOwnerId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [locations, setlocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // Add loading state

  const serviceTypeOptions = vehicleTypes.map((type) => ({
    value: type._id,
    label: type.name,
  }));

  const serviceLocationOptions = locations.map((location) => ({
    value: location._id,
    label: location.name,
  }));

  useEffect(() => {
    if (vehiclesData) {
      setAddVehicleData({
        ...vehiclesData,
        serviceType: vehiclesData.type,
      });
    } else {
      setAddVehicleData(initialData);
    }

    getAllVehicleTypes()
      .then((res) => {
        setVehicleTypes(res?.data || []);
      })
      .catch((error) => {
        console.error("Error fetching vehicle types:", error);
      });

    getActiveMobileLocations()
      .then((res) => {
        setlocations(res?.data || []);
      })
      .catch((error) => {
        console.error("Error fetching vehicle types:", error);
      });
  }, [vehiclesData]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    console.log("image is", file);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddVehicleData({
      ...addVehicleData,
      [name]: value,
    });
  };

  useEffect(() => {
    getCurrentUser()
      .then((res) => {
        setOwnerId(res.data.user.ownerId);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true); // Set loading state to true

    const formData = new FormData();
    formData.append(
      "image",
      selectedFile !== null ? selectedFile : addVehicleData?.image
    );
    formData.append("brand", addVehicleData?.brand);
    formData.append("model", addVehicleData?.model);

    const matchedType = serviceTypeOptions.find(
      (option) => option.label === addVehicleData?.serviceType
    );
    formData.append("type", matchedType?.value || addVehicleData?.serviceType);

    formData.append("registrationNumber", addVehicleData?.registrationNumber);

    const matchedLocation = serviceLocationOptions.find(
      (option) => option.label === addVehicleData?.location
    );
    formData.append(
      "location",
      matchedLocation?.value || addVehicleData?.location
    );

    formData.append("owner", ownerId);

    try {
      if (vehiclesData._id) {
        await updateVehicle(vehiclesData._id, formData);
        success("Successful", "Mobile Location Updated Successfully");
      } else {
        await addVehicle(formData);
        success("Successful", "Mobile Location Added Successfully");
      }

      setAddVehicleData(initialData);
      setSelectedFile(null);
      fetchVehicles();
      setOpen(false);
    } catch (err) {
      errorMsg("Oops", err?.response?.data?.message);
      console.log("Error is", err);
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="z-50" onClose={setOpen}>
        <Transition.Child
          enter="ease-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-300"
          leaveFrom="opacity-100 transform scale-100"
          leaveTo="opacity-0 transform scale-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-500"
              enterFrom="opacity-0 transform scale-0"
              enterTo="opacity-100 transform scale-100"
              leave="ease-in duration-300"
              leaveFrom="opacity-100 transform scale-100"
              leaveTo="opacity-0 transform scale-0"
            >
              <Dialog.Panel className="mt-12 lg:ml-60 md:ml-60 ml-0 z-50 lg:w-1/2 md:w-1/2 w-4/5 fixed mx-auto rounded-lg bg-white pt-5 pb-4 text-left shadow-xl transition-all h-auto">
                <div className="absolute top-0 right-0 pt-4 pr-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                    onClick={() => setOpen(false)}
                    disabled={isLoading} // Disable close button when loading
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="">
                  <div className=" sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="px-3 pb-5 text-xl font-medium border-b-2 border-gray-200 leading-6 text-gray-700"
                    >
                      {vehiclesData?._id
                        ? "Update Mobile Location"
                        : "Add Mobile Location"}
                    </Dialog.Title>
                    <div className="">
                      <form onSubmit={handleSubmit} method="POST">
                        <div className="h-[30rem] overflow-y-scroll">
                          <div className="px-7 py-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <h2 className="text-lg font-semibold text-gray-300 ">
                                  Mobile Location Information
                                </h2>
                              </div>
                            </div>
                            <div>
                              <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                <div className="lg:col-span-5 md:col-span-5 sm:col-span-12">
                                  <label
                                    htmlFor="vehicleImage"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Image
                                  </label>
                                </div>
                                <div className="text-left lg:col-span-7 md:col-span-7 sm:col-span-12">
                                  <div className="w-1/3 mt-2">
                                    <label
                                      htmlFor="image"
                                      className="mx-auto bg-gray-100 rounded-lg h-28 w-28 inset-0 flex items-center justify-center cursor-pointer overflow-hidden"
                                    >
                                      {selectedFile ||
                                      (vehiclesData?._id &&
                                        vehiclesData?.image) ? (
                                        <img
                                          src={
                                            selectedFile
                                              ? URL.createObjectURL(
                                                  selectedFile
                                                )
                                              : `${baseURL}/s3/images/${addVehicleData?.image}`
                                          }
                                          alt="Selected"
                                          className="h-full w-full object-cover"
                                        />
                                      ) : (
                                        <LuImagePlus className="text-gray-400 h-14 w-14 p-2" />
                                      )}
                                    </label>
                                    <input
                                      type="file"
                                      name="image"
                                      id="image"
                                      className="hidden"
                                      onChange={handleFileChange}
                                      disabled={isLoading} // Disable file input when loading
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                <div className="lg:col-span-5 md:col-span-5 sm:col-span-12 ">
                                  <label
                                    htmlFor="brand"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Brand{" "}
                                    <span className="text-red-500 ">*</span>
                                  </label>
                                </div>
                                <div className="lg:col-span-7 md:col-span-7 sm:col-span-12 ">
                                  <div className="mt-2">
                                    <input
                                      type="text"
                                      name="brand"
                                      placeholder="Enter Vehicle Brand"
                                      value={addVehicleData?.brand}
                                      onChange={handleChange}
                                      id="brand"
                                      className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellow sm:leading-6 focus:ring-transparent"
                                      required
                                      disabled={isLoading} // Disable input when loading
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                <div className="lg:col-span-5 md:col-span-5 sm:col-span-12 ">
                                  <label
                                    htmlFor="model"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Model{" "}
                                    <span className="text-red-500 ">*</span>
                                  </label>
                                </div>
                                <div className="lg:col-span-7 md:col-span-7 sm:col-span-12 ">
                                  <div className="mt-2">
                                    <input
                                      type="text"
                                      name="model"
                                      placeholder="Enter Vehicle Model"
                                      value={addVehicleData?.model}
                                      onChange={handleChange}
                                      id="model"
                                      className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellow sm:leading-6 focus:ring-transparent"
                                      required
                                      disabled={isLoading} // Disable input when loading
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                <div className="lg:col-span-5 md:col-span-5 sm:col-span-12 ">
                                  <label
                                    htmlFor="registrationNumber"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Registration Number{" "}
                                    <span className="text-red-500 ">*</span>
                                  </label>
                                </div>
                                <div className="lg:col-span-7 md:col-span-7 sm:col-span-12 ">
                                  <div className="mt-2">
                                    <input
                                      type="text"
                                      name="registrationNumber"
                                      placeholder="Enter Registration Number"
                                      value={addVehicleData?.registrationNumber}
                                      onChange={handleChange}
                                      id="registrationNumber"
                                      className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellow sm:leading-6 focus:ring-transparent"
                                      required
                                      disabled={isLoading} // Disable input when loading
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                <div className="lg:col-span-5 md:col-span-5 sm:col-span-12">
                                  <label
                                    htmlFor="service-type"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Select Service / Type{" "}
                                    <span className="text-red-500">*</span>
                                  </label>
                                </div>
                                <div className="lg:col-span-7 md:col-span-7 sm:col-span-12">
                                  <div className="mt-2">
                                    <Select
                                      name="service-type"
                                      id="select-service-type"
                                      options={serviceTypeOptions}
                                      value={serviceTypeOptions.find(
                                        (option) =>
                                          option.label ===
                                            addVehicleData?.serviceType ||
                                          option.value ===
                                            addVehicleData?.serviceType
                                      )}
                                      className="basic-multi-select"
                                      classNamePrefix="select Service Type"
                                      onChange={(selectedOption) => {
                                        setAddVehicleData({
                                          ...addVehicleData,
                                          serviceType: selectedOption?.label,
                                        });
                                      }}
                                      required
                                      isDisabled={isLoading} // Disable select when loading
                                    />
                                  </div>
                                </div>
                              </div>{" "}
                              <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                <div className="lg:col-span-5 md:col-span-5 sm:col-span-12 ">
                                  <label
                                    htmlFor="location"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Select Location{" "}
                                    <span className="text-red-500 ">*</span>
                                  </label>
                                </div>
                                <div className="lg:col-span-7 md:col-span-7 sm:col-span-12 ">
                                  <div className="mt-2">
                                    <Select
                                      name="location"
                                      options={serviceLocationOptions}
                                      value={serviceLocationOptions.find(
                                        (option) =>
                                          option.label ===
                                            addVehicleData?.location ||
                                          option.value ===
                                            addVehicleData?.location
                                      )}
                                      className="basic-multi-select"
                                      classNamePrefix="select location"
                                      onChange={(selectedOption) => {
                                        setAddVehicleData({
                                          ...addVehicleData,
                                          location: selectedOption?.label,
                                        });
                                      }}
                                      required
                                      isDisabled={isLoading} // Disable select when loading
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="border-t-2 border-gray-200 px-7 pt-3 flex items-center justify-end">
                          <button
                            type="submit"
                            className="flex justify-center rounded-md bg-yellow px-3 py-2.5 px-10 text-base font-semibold leading-6 text-dark shadow-sm hover:bg-opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isLoading} // Disable button when loading
                          >
                            {isLoading ? (
                              // Loading spinner
                              <svg
                                className="animate-spin -ml-1 mr-3 h-5 w-5 text-dark"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                            ) : null}
                            {isLoading
                              ? vehiclesData?._id
                                ? "Updating..."
                                : "Submitting..."
                              : vehiclesData?._id
                              ? "Update"
                              : "Submit"}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AddVehicle;
